
<template lang="pug">
div
    easy-data-table(
        ref="dataTable"
        v-model:server-options="serverOptionsModel"
        table-class-name="tl-table"
        :headers="headers"
        :items="items"
        :rows-per-page="rowsPerPage"
        :current-page-pagination="currentPage"
        :server-items-length="serverItemsLength"
        :body-row-class-name="getRowClassName"
        :loading="loading"
        hide-footer
        v-bind="tableOptions"
        @click-row="($event: ClickRowArgument) => emit('click-row', $event)"
        @update-sort="updateSort"
    )
        template(
            v-for="(_, name) in slots"
            #[name]="slotData"
        )
            slot(
                :name="name"
                v-bind="slotData || {}"
            )
        template(#loading)
            tl-loading-dots(
                class="tl-table-loading"
                size="10"
                color="black"
            )
    tl-pagination(
        v-if="serverOptions || paginationLength > 1"
        v-model="currentPage"
        v-model:items-per-page="currentRowsPerPage"
        class="tl-table-pagination"
        :items-length="serverItemsLength"
        :disabled="loading"
        per-page-control
    )
</template>

<script lang="ts" setup>
import EasyDataTable from 'vue3-easy-data-table'
import type { Header, ServerOptions, Item, ClickRowArgument } from 'vue3-easy-data-table'
import { computed, ref, useSlots, watch } from 'vue'
import { clone } from 'widget-components/utilities/clone'
import equals from 'fast-deep-equal/es6'

type Props = {
    headers: Header[]
    items: Item[]
    rowsPerPage?: number
    serverItemsLength?: number
    serverOptions?: {
        page: ServerOptions['page']
        rowsPerPage?: ServerOptions['rowsPerPage']
        sortBy?: ServerOptions['sortBy'] | null
        sortType?: ServerOptions['sortType'] | null
    }
    tableOptions?: Record<string, unknown>
    loading?: boolean
}

//there are no types for data table component
const dataTable = ref<unknown>(null)

const slots = useSlots()

const props = withDefaults(defineProps<Props>(), {
    rowsPerPage: 20,
    loading: false,
})

const emit = defineEmits<{
    (e: 'update:server-options', value: Props['serverOptions']): void
    (e: 'click-row', item: ClickRowArgument): void
}>()

const serverOptionsModel = ref(clone(props.serverOptions))

const innerPage = ref(1)
const currentPage = computed<number>({
    get: () => {
        if (serverOptionsModel.value) {
            return serverOptionsModel.value.page
        }
        return innerPage.value
    },
    set: (val: number) => {
        if (serverOptionsModel.value) {
            serverOptionsModel.value.page = val
            return
        }
        innerPage.value = val;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (dataTable as any).value.updatePage(innerPage.value)
    },
})

const rowsPerPageModel = ref<number>(props.rowsPerPage)

const currentRowsPerPage = computed<number>({
    get: () => {
        return serverOptionsModel.value?.rowsPerPage ? serverOptionsModel.value.rowsPerPage : rowsPerPageModel.value
    },
    set: (val: number) => {
        if (serverOptionsModel.value) {
            serverOptionsModel.value.rowsPerPage = val
            return
        }
        rowsPerPageModel.value = val

    },
})

const paginationLength = computed(() => {
    return Math.ceil(props.items.length / props.rowsPerPage)
})

const updateSort = () => {
    if (serverOptionsModel.value) {
        return
    }
    currentPage.value = 1
}

const getRowClassName = (_: string, rowNumber: string) => `tl-table-row row-${rowNumber}`

//in
watch(() => props.serverOptions, (val) => {
    if (!equals(serverOptionsModel.value, val)) {
        serverOptionsModel.value = clone(props.serverOptions)
    }
}, { deep: true },
)

//out
watch(serverOptionsModel, (val) => {
    emit('update:server-options', val)
}, { deep: true })
</script>

<style lang="sass">
@import 'vue3-easy-data-table/dist/style.css'

.tl-table
    --easy-table-border: 1px solid var(--color-gray-tint-normal)
    --easy-table-row-border: 1px solid var(--color-gray-tint-normal)

    --easy-table-header-font-size: 14px
    --easy-table-header-height: 36px
    --easy-table-header-font-color: var(--color-black)
    --easy-table-header-background-color: var(--color-gray-tint-normal)

    --easy-table-header-item-padding: 8px 8px

    --easy-table-body-even-row-font-color: var(--color-white)
    --easy-table-body-even-row-background-color: #4c5d7a

    --easy-table-body-row-font-color: var(--color-black)
    --easy-table-body-row-background-color: var(--color-white)
    --easy-table-body-row-height: 60px
    --easy-table-body-row-font-size: 14px

    --easy-table-body-row-hover-font-color: var(--easy-table-body-row-font-color)
    --easy-table-body-row-hover-background-color: var(--easy-table-body-row-background-color)

    --easy-table-body-item-padding: 16px 12px

    // --easy-table-scrollbar-track-color: #2d3a4f
    // --easy-table-scrollbar-color: #2d3a4f
    // --easy-table-scrollbar-thumb-color: #4c5d7a
    // --easy-table-scrollbar-corner-color: #2d3a4f

    --easy-table-loading-mask-background-color: var(--color-black-tint-light)
    --tl-table-td-cursor: pointer

    border-radius: var(--border-radius)

    .header-text
        line-height: 1.4em
        font-weight: 700

    td
        vertical-align: middle
        cursor: var(--tl-table-td-cursor)

    &-pagination
        margin-top: 16px
    &-loading
        padding-top: var(--easy-table-header-height)

    .sortType-icon
        border-width: 4px !important
        border-bottom-width: 5px!important
</style>
